import React from 'react';
import I18n from 'i18n-js';

import { SanitizedRender } from '@reverbdotcom/commons';
import { RCToast } from '@reverbdotcom/cadence/components';
import { ToastCloseType } from '@reverbdotcom/cadence/components/types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { buildClickedAddFavoriteSearch, buildClickedRemoveFavoriteSearch, buildClickedToastMessage } from '@reverbdotcom/commons/src/event_tracking/favorites';
import { mapFavoriteToEntityType } from '@reverbdotcom/commons/src/favorites/entity_type';
import { MutationResult } from '@reverbdotcom/commons/src/useMutation';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';

import { Paths } from '../shared/url_helpers';
import { useFindFavorite, ExternalProps, FavoriteModal } from './find_favorite_context';
import FavoriteButton from './favorite_button';
import SaveFavoriteModal from './favorite_modal';
import { CoreFindFavorite, UpsertMyFavorite } from '../../schemas/rql';
import { RemoveFavoriteModal } from './remove_favorite_modal';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { LoggedOutFavoriteButton } from './logged_out_favorite_button';
interface FavoriteButtonWrapperProps {
  favorite: CoreFindFavorite.Favorite;
  displayStyle?: 'banner' | 'button';
  children: JSX.Element;
  header?: string;
  subtext?: string;
}

function FavoriteButtonWrapper(props: FavoriteButtonWrapperProps) {
  if (props.displayStyle !== 'banner') {
    return props.children;
  }

  let bannerHeaderContent = props.header || I18n.t('discovery.addFollowToFeed.letGearComeToYou');
  if (props.favorite.favorited) {
    const searchableTypeContent = I18n.t(`discovery.addFollowToFeed.followBannerType.${mapFavoriteToEntityType(props.favorite)}`);
    bannerHeaderContent = I18n.t('discovery.addFollowToFeed.youAreFollowingHtml', {
      type: searchableTypeContent,
      spanOpen: '<span class="tt-capitalize">',
      spanClose: '</span>',
    });
  }

  return (
    <div className="feed-button__banner">
      <h4 className="size-150 mobile-size-130 weight-bold pb-half">
        <SanitizedRender html={bannerHeaderContent} />
      </h4>
      <p className="size-90">
        {props.subtext || I18n.t('discovery.favorites.saveSearchCallout')}
      </p>
      {props.children}
    </div>
  );
}

export function FavoriteButtonContainer(props: ExternalProps) {
  const {
    displayedModal,
    setDisplayedModal,
    isToastOpen,
    setIsToastOpen,
    favorite,
    setFavorite,
    canFavorite,
  } = useFindFavorite();
  const url = useURL();
  const user = useUser();

  const updateFavorite = function (key: 'feedEnabled' | 'emailEnabled', value: boolean) {
    const newFavorite = { ...favorite, [key]: value };

    if (key === 'feedEnabled' && !value) {
      newFavorite.emailEnabled = false;
    }

    setFavorite(newFavorite);
  };

  const handleUpsert = function (result: MutationResult<UpsertMyFavorite.Mutation>) {
    const newFavorite: CoreFindFavorite.Favorite = {
      ...favorite,
      id: result.data.upsertMyFavorite?.favorite?.id,
      favorited: result.data.upsertMyFavorite?.favorite?.favorited,
    };
    trackEvent(buildClickedAddFavoriteSearch(newFavorite, url));
    setFavorite(newFavorite);
    setIsToastOpen(true);
  };

  const handleRemove = function () {
    const newFavorite: CoreFindFavorite.Favorite = {
      ...favorite,
      favorited: false,
    };
    trackEvent(buildClickedRemoveFavoriteSearch(newFavorite, url));
    setFavorite(newFavorite);
  };

  const handleOpenModal = function () {
    setDisplayedModal(favorite.favorited ? FavoriteModal.Remove : FavoriteModal.Save);
  };

  const handleToastClose = function (closeType: ToastCloseType) {
    setIsToastOpen(false);

    if (closeType === 'dismiss' || closeType === 'swipe') {
      trackEvent(buildClickedToastMessage(url));
    }
  };

  return (
    <div className="favorite-button">
      <FavoriteButtonWrapper
        favorite={favorite}
        displayStyle={props.displayStyle}
        header={props.header}
        subtext={props.subtext}
      >
        {
          user.loggedOut ?
            <LoggedOutFavoriteButton
              entityType={mapFavoriteToEntityType(favorite)}
              modalOpen={displayedModal === FavoriteModal.Signup}
              renderWithBanner={props.displayStyle === 'banner'}
              disabled={!canFavorite}
              onModalClose={() => { setDisplayedModal(FavoriteModal.None); }}
            />
            :
            <FavoriteButton
              renderWithBanner={props.displayStyle === 'banner'}
              entityType={mapFavoriteToEntityType(favorite)}
              openModal={handleOpenModal}
              disabled={!canFavorite}
              isFavorited={favorite.favorited}
              hideToast={() => { setIsToastOpen(false); }}
            />
        }
      </FavoriteButtonWrapper>
      <SaveFavoriteModal
        isOpen={displayedModal === FavoriteModal.Save}
        handleClose={() => { setDisplayedModal(FavoriteModal.None); }}
        handleUpdate={updateFavorite}
        modalTitle={I18n.t('discovery.favorites.confirmModalTitle')}
        searchTitle={favorite.title}
        feedEnabled={favorite.feedEnabled}
        emailEnabled={favorite.emailEnabled}
        queryParams={favorite.queryParams}
        handleMutationResult={handleUpsert}
      />
      <RemoveFavoriteModal
        handleRemove={handleRemove}
      />
      {/*
        This renders separate RCToast components for the add and remove states,
        because the add version has a link prop and the remove version does not.
        Using 1 RCToast with a conditional link prop causes the toast to not render when the link is null.
      */}
      <RCToast
        key="favorite--add"
        theme="watch-add"
        isOpen={isToastOpen && favorite.favorited}
        onClose={handleToastClose}
        text={I18n.t('discovery.favorites.toast.added')}
        link={
          <CoreLink
            to={Paths.myFavoritesSearches.expand({})}
            clickEvent={buildClickedToastMessage(url)}
          >
            {I18n.t('cadence.RCToast.linkDefault')}
          </CoreLink>
        }
      />
      <RCToast
        key="favorite--remove"
        theme="watch-remove"
        isOpen={isToastOpen && !favorite.favorited}
        onClose={handleToastClose}
        text={I18n.t('discovery.favorites.toast.removed')}
      />
    </div>
  );
}
